/**
 * Panel host integration: the mot-r Chrome extension's side panel and the Outlook add-in's task
 * pane both frame the app at "/jobs/new-workflow?host=extension|outlook" (see
 * chrome-extension/panel-host.js). In that mode the app renders the page alone — no sidebar or
 * header — because the panel is narrow and only offers starting a job. The flag is remembered
 * for the session because routing drops the query.
 *
 * Sign-in: the extension uses the generic iframe popup in AuthGuard. Outlook desktop blocks
 * popups, so there the task pane opens an Office dialog on "/?popup=login&host=outlook" (see
 * requestPanelSignIn / completeOutlookAuthPopup), like the add-in always signed in.
 */
import { mergeTheme, parseTenantThemeOverride } from '@/theme/themeUtils';

export type PanelHost = 'extension' | 'outlook';

const PANEL_HOST_KEY = 'motr.host.panel';
const OUTLOOK_AUTH_POPUP_KEY = 'motr.outlook.authPopup';
// The task pane's one-time code for this sign-in dialog, and the hand-off for the relay page.
const OUTLOOK_NONCE_KEY = 'motr.outlook.nonce';
const OUTLOOK_RELAY_KEY = 'motr.outlook.relay';

/** The mot-r Chrome extension (id pinned by the "key" in chrome-extension/manifest.json). */
export const EXTENSION_ORIGIN = 'chrome-extension://nepfchbjgjpmlkfjclgdlljgcebabbcf';

const params = new URLSearchParams(window.location.search);
const urlHost = params.get('host');
const isOutlookPopupStart = params.get('popup') === 'login' && urlHost === 'outlook';
const hostOnLoad: PanelHost | null =
  !isOutlookPopupStart && (urlHost === 'extension' || urlHost === 'outlook') ? urlHost : null;

// Captured at import time, before the router or the auth callback rewrites the URL.
try {
  if (hostOnLoad) sessionStorage.setItem(PANEL_HOST_KEY, hostOnLoad);
  if (isOutlookPopupStart) {
    sessionStorage.setItem(OUTLOOK_AUTH_POPUP_KEY, '1');
    const nonce = params.get('nonce');
    if (nonce) sessionStorage.setItem(OUTLOOK_NONCE_KEY, nonce);
    else sessionStorage.removeItem(OUTLOOK_NONCE_KEY);
  }
} catch { /* storage blocked: the URL flag still works for this load */ }

/**
 * Framed by mot-r's own Outlook task pane page (/outlook-addin/…, same origin). The mot-r tab in
 * Outlook / Teams is framed by Microsoft's page instead, which is cross-origin and cannot be read.
 */
export const isInOutlookTaskPane = (): boolean => {
  if (window.parent === window) return false;
  try {
    return window.parent.location.origin === window.location.origin &&
      window.parent.location.pathname.startsWith('/outlook-addin/');
  } catch {
    return false;
  }
};

/** Which panel frames the app, or null for the normal web app. */
export const panelHostKind = (): PanelHost | null => {
  if (hostOnLoad) return hostOnLoad;
  // A load that names another host (the Teams / Microsoft 365 tab) is never a panel.
  if (urlHost === 'teams') return null;
  // Only inside a frame: a window opened from the panel can inherit its session storage, and
  // must show the normal app.
  if (window.parent === window) return null;
  try {
    const stored = sessionStorage.getItem(PANEL_HOST_KEY);
    // In Outlook the add-in pane and the mot-r tab are frames of one window on the same address,
    // so they share session storage: the pane's flag must not turn the tab into a panel (no
    // header, no menu). The pane's own frame is recognisable by its parent page.
    if (stored === 'outlook') return isInOutlookTaskPane() ? 'outlook' : null;
    return stored === 'extension' ? stored : null;
  } catch {
    return null;
  }
};

/** Running inside the Chrome extension's side panel or the Outlook add-in's task pane. */
export const isExtensionHost = (): boolean => panelHostKind() !== null;

/**
 * The only origin the app talks to as its panel. The Outlook task pane is served by mot-r from
 * the same address as the app.
 */
export const panelParentOrigin = (): string =>
  panelHostKind() === 'outlook' ? window.location.origin : EXTENSION_ORIGIN;

const fromPanel = (event: MessageEvent) => event.origin === panelParentOrigin() && event.source === window.parent;

const postToPanel = (message: Record<string, unknown>) => {
  if (!isExtensionHost() || window.parent === window) return;
  try { window.parent.postMessage(message, panelParentOrigin()); } catch { /* panel gone */ }
};

// Tells the panel the app itself loaded (signed in or not). Without it the panel cannot tell
// mot-r apart from an error page (offline server, dropped tunnel) and offers a retry instead.
postToPanel({ type: 'motr:extension:loaded' });

/** The email open next to the panel (Gmail or Outlook), as the panel hands it over. */
export interface ExtensionEmail {
  subject?: string;
  senderEmail?: string;
  senderName?: string;
  /** The original RFC 822 message, when Gmail returned it. */
  rawMessageBase64?: string;
  /** The email's text as the mail client shows it, when the original was unavailable or too large. */
  bodyText?: string;
  /** ISO date, when there is no original (Outlook without getAsFileAsync). */
  date?: string;
  /** Attachments with their content, when there is no original to read them from. */
  attachments?: { fileName: string; contentType?: string; size?: number; contentBase64?: string }[];
  /** Attachments the mail client could not hand over, with the reason (shown to the user). */
  skippedAttachments?: string[];
}

/**
 * Asks the side panel for the email open next to it. Resolves null when no email is open, when
 * not running in the panel, or when the panel does not answer in time. Only messages from the
 * extension frame that hosts this page are accepted.
 */
let emailRequestSeq = 0;

export const requestExtensionEmail = (timeoutMs = 20000): Promise<ExtensionEmail | null> => {
  if (!isExtensionHost() || window.parent === window) return Promise.resolve(null);
  // Each request carries an id the panel echoes, so an answer meant for an earlier request
  // (an email the user has since switched away from) is never taken for this one.
  const requestId = `${Date.now()}-${++emailRequestSeq}`;
  return new Promise((resolve) => {
    const done = (email: ExtensionEmail | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(email);
    };
    const onMessage = (event: MessageEvent) => {
      if (!fromPanel(event)) return;
      if (event.data?.type !== 'motr:extension:email') return;
      // A host from before request ids (an extension not yet updated) answers without one.
      if (event.data.requestId !== undefined && event.data.requestId !== requestId) return;
      done((event.data.email as ExtensionEmail | null) ?? null);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    window.addEventListener('message', onMessage);
    postToPanel({ type: 'motr:extension:ready', requestId });
  });
};

/** The firm a Gmail address belongs to, from mot-r's admin-maintained email-domain mapping. */
export interface ExtensionFirm {
  /** The tenant to sign in to; '' means the mot-r host account. */
  tenantId: string;
  /** What the firm is called on screen (its display name). */
  name: string;
  /** The tenant's own name (as in its subdomain), which names its default logo. */
  tenantName?: string;
}

/** The mailbox address next to the panel (Gmail tab or Outlook mailbox), or null. */
const requestExtensionAccount = (timeoutMs = 5000): Promise<string | null> => {
  if (!isExtensionHost() || window.parent === window) return Promise.resolve(null);
  return new Promise((resolve) => {
    const done = (email: string | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(email);
    };
    const onMessage = (event: MessageEvent) => {
      if (!fromPanel(event)) return;
      if (event.data?.type !== 'motr:extension:account') return;
      done(typeof event.data.email === 'string' && event.data.email ? event.data.email : null);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    window.addEventListener('message', onMessage);
    postToPanel({ type: 'motr:extension:account' });
  });
};

/**
 * Which firm to sign in to, worked out from the Gmail address next to the panel (the same
 * domain mapping the Outlook and Gmail add-ins use). Null when there is no Gmail address or the
 * domain is not mapped; the login page then lets the user choose as usual.
 */
export const findExtensionFirm = async (apiUrl: string): Promise<ExtensionFirm | null> => {
  const email = await requestExtensionAccount();
  return email ? discoverFirmByEmail(apiUrl, email) : null;
};

/**
 * The firm a mailbox address belongs to, from mot-r's email-domain mapping (/api/tenant-discovery):
 * a firm, the host account ('' tenant), or null when the domain is not mapped.
 */
export const discoverFirmByEmail = async (apiUrl: string, email: string): Promise<ExtensionFirm | null> => {
  try {
    const response = await fetch(`${apiUrl}/api/tenant-discovery`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return null;
    const result = (await response.json()) as {
      tenant?: { tenantId?: string; displayName?: string; name?: string } | null;
      isHost?: boolean;
    };
    if (result.tenant?.tenantId) {
      return {
        tenantId: result.tenant.tenantId,
        name: result.tenant.displayName || result.tenant.name || '',
        tenantName: result.tenant.name || '',
      };
    }
    return result.isHost ? { tenantId: '', name: 'mot-r' } : null;
  } catch {
    return null;
  }
};

const BRANDING_SETTINGS = [
  'motr.Settings.ThemeOverride',
  'motr.Settings.CompanyName',
  'motr.Settings.Logo',
  'motr.Settings.LogoUrl',
  'motr.Settings.BlobStorageUrl',
];

/**
 * The firm's branding before anyone signs in, for the sign-in card and the panel's top bar: the
 * same logo, name and sidebar colours the app shows once signed in (see PanelBrandingReporter),
 * read with the anonymous settings and logo endpoints the login page uses. Null for the mot-r
 * host account or when nothing can be read; the card then keeps mot-r's own look.
 */
export const fetchExtensionFirmBranding = async (
  apiUrl: string,
  firm: ExtensionFirm,
): Promise<PanelBranding | null> => {
  if (!firm.tenantId) return null;
  const headers = { Accept: 'application/json', __tenant: firm.tenantId };
  const getJson = async <T,>(path: string): Promise<T | null> => {
    try {
      const response = await fetch(`${apiUrl}${path}`, { credentials: 'omit', headers });
      return response.ok ? ((await response.json()) as T) : null;
    } catch {
      return null;
    }
  };
  const [settingList, logos] = await Promise.all([
    getJson<Array<{ name: string; value: string }>>(
      `/api/app/settings/settings?names=${encodeURIComponent(BRANDING_SETTINGS.join(','))}`,
    ),
    getJson<{ lightLogoUrl: string | null; darkLogoUrl: string | null }>('/api/app/branding/public/logo-urls'),
  ]);
  if (!settingList && !logos) return null;
  const setting = (name: string) => settingList?.find((s) => s.name === name)?.value || '';

  const sidebar = mergeTheme(parseTenantThemeOverride(setting('motr.Settings.ThemeOverride'))).sidebar;
  const dark = sidebar.sidebarMode === 'dark';
  const lightLogo = sidebar.logoLightUrl || logos?.lightLogoUrl;
  const darkLogo = sidebar.logoDarkUrl || logos?.darkLogoUrl;
  const themed = dark ? (lightLogo || darkLogo) : (darkLogo || lightLogo);
  // Without uploaded logos the app shows "<blob>/www/<tenant name>_Logo.png" (TenantProvider).
  const blob = (setting('motr.Settings.BlobStorageUrl') || 'https://motrv2assets.blob.core.windows.net').replace(/\/+$/, '');
  const fallbackLogo = setting('motr.Settings.Logo') || setting('motr.Settings.LogoUrl')
    || (firm.tenantName ? `${blob}/www/${encodeURIComponent(firm.tenantName)}_Logo.png` : '');
  let logoUrl: string | null = null;
  try { logoUrl = themed || fallbackLogo ? new URL(themed || fallbackLogo, window.location.origin).href : null; } catch { /* bad URL */ }

  return {
    name: setting('motr.Settings.CompanyName') || firm.name || 'mot-r',
    logoUrl,
    // The default logo is a single-colour mark: shown white on a dark sidebar, as in the app.
    logoFilter: !themed && fallbackLogo && dark ? 'brightness(0) invert(1)' : null,
    background: sidebar.background,
    text: sidebar.text,
  };
};

// In the panel's (partitioned) storage: the user signed out, so the next sign-in must ask for
// the password even though mot-r's login cookie in the popup is still valid.
const SIGNED_OUT_KEY = 'motr.extension.signedOut';

export const markExtensionSignedOut = (): void => {
  try { localStorage.setItem(SIGNED_OUT_KEY, '1'); } catch { /* storage blocked */ }
};

export const isExtensionSignedOut = (): boolean => {
  try { return localStorage.getItem(SIGNED_OUT_KEY) === '1'; } catch { return false; }
};

export const clearExtensionSignedOut = (): void => {
  try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ }
};

/**
 * Tells the side panel which screen is showing, so it knows how to treat another email opened in
 * Gmail: the first screen takes it straight away ('start'), the wizard asks first ('wizard'), and
 * after "Job created" there is nothing to lose ('done').
 */
export const reportExtensionStage = (stage: 'start' | 'wizard' | 'done'): void => {
  postToPanel({ type: 'motr:extension:stage', stage });
};

/** Sign-in result handed back by the panel: the OIDC user for this frame's storage. */
export interface PanelSignInResult {
  oidcStorageKey: string;
  oidcUserJson: string;
}

/**
 * Outlook: asks the task pane to run sign-in in an Office dialog (Outlook desktop blocks
 * popups) and resolves with the signed-in user. tenant: the firm ('' = host), prompt: "login"
 * to ask for the password again.
 */
export const requestPanelSignIn = (tenant: string | null, prompt?: string): Promise<PanelSignInResult> =>
  new Promise((resolve, reject) => {
    const onMessage = (event: MessageEvent) => {
      if (!fromPanel(event)) return;
      if (event.data?.type === 'motr:extension:signedIn' && event.data.oidcStorageKey && event.data.oidcUserJson) {
        window.removeEventListener('message', onMessage);
        resolve({ oidcStorageKey: event.data.oidcStorageKey, oidcUserJson: event.data.oidcUserJson });
      } else if (event.data?.type === 'motr:extension:signInFailed') {
        window.removeEventListener('message', onMessage);
        reject(new Error(event.data.message || 'Sign-in failed.'));
      }
    };
    window.addEventListener('message', onMessage);
    postToPanel({ type: 'motr:extension:signIn', tenant, prompt });
  });

/** This window is the Outlook sign-in dialog (from its first load until the sign-in completes). */
export const isOutlookAuthPopup = (): boolean => {
  if (isOutlookPopupStart) return true;
  try { return sessionStorage.getItem(OUTLOOK_AUTH_POPUP_KEY) === '1'; } catch { return false; }
};

/** True only on the Outlook sign-in dialog's first load, before the OIDC redirect. */
export const isOutlookAuthPopupStart = (): boolean => isOutlookPopupStart;

/**
 * Outlook sign-in dialog, signed in: go to the add-in's relay page on the same origin, which
 * hands the signed-in user to the task pane with Office's messageParent (this app does not load
 * office.js). The relay only acts on this one-time hand-off: the exact session key plus the
 * pane's code for this dialog, which the pane checks before accepting the session.
 */
export const completeOutlookAuthPopup = (oidcStorageKey: string): void => {
  try {
    const nonce = sessionStorage.getItem(OUTLOOK_NONCE_KEY);
    sessionStorage.removeItem(OUTLOOK_AUTH_POPUP_KEY);
    sessionStorage.removeItem(OUTLOOK_NONCE_KEY);
    if (nonce) sessionStorage.setItem(OUTLOOK_RELAY_KEY, JSON.stringify({ nonce, oidcStorageKey }));
  } catch { /* storage blocked: the relay reports the failure */ }
  window.location.replace(`${window.location.origin}/outlook-addin/react-signin-done.html`);
};

/** The firm's branding for the panel's top bar: what the app's sidebar shows. */
export interface PanelBranding {
  name: string;
  logoUrl: string | null;
  /** CSS filter for the logo (a single-colour logo shown white on a dark bar). */
  logoFilter?: string | null;
  background: string;
  text: string;
}

/**
 * Opens a mot-r page (same origin) in the user's browser. In Outlook the pane does it with
 * Office's openBrowserWindow (window.open is unreliable in its webviews); returns false when the
 * caller should follow its own link instead (Chrome side panel, web app).
 */
export const openInBrowserViaPanel = (url: string): boolean => {
  if (panelHostKind() !== 'outlook' || window.parent === window) return false;
  postToPanel({ type: 'motr:extension:openUrl', url });
  return true;
};

/** Hands the firm's branding to the panel, which styles its own top bar with it. */
export const reportPanelBranding = (branding: PanelBranding): void => {
  postToPanel({ type: 'motr:extension:branding', branding });
};
